import React, { useState } from 'react';
import styled from 'styled-components';
import { IFiltersListCmp } from './IFiltersListCmp';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { useTranslation } from 'next-i18next';
import { LinkHandler, Typography } from '@/components/atoms';
import {
  FilterLink,
  AccordionFilter,
  RangeSelector,
} from '@/components/molecules';
import { makeCrossFilterHref } from '@/utils/Catalog';
import { capitalize } from '@/utils/index';
import { UNLIMITED_DRAWER } from '@/utils/Filters';
import { ICmsTagsGroup } from '@/types/collections';
import { IColorFields } from '@/types/contentful/contentful';
import { useRouter } from 'next/router';

interface StyleProps {
  noMaxHeight?: boolean;
}

const FilterLinkWrapper = styled.div`
  width: 100%;
  * {
    &:last-child {
      margin-bottom: 0;
    }
  }
`;
const AccordionContentWrapper = styled.div<StyleProps>`
  padding: 4px 0;
  max-height: ${props => (props.noMaxHeight ? 'none' : '240px')};
  overflow: auto;
`;
const ColorsContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  row-gap: 8px;
`;

const RangeSelectorWrapper = styled.div`
  padding: 4px 8px;
  margin-bottom: 24px;
`;

const Content = styled.div`
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
`;

const RemoveLinkWrapper = styled.div`
  /* font-size: 14px; */
  padding: 0 4px;
  margin-bottom: 16px;
  cursor: pointer;
  & a {
    color: ${props => props.theme.paletteDefault.primary.main};
    text-decoration: underline;
  }
`;

const RemoveFiltersTypography = styled(Typography)`
  color: ${props => props.theme.paletteDefault.grey1};
`;

const FiltersList: React.FC<IFiltersListCmp> = ({
  filters,
  slug,
  queryParameters,
}) => {
  const { t } = useTranslation(['common', 'catalog']);
  const [expanded, setExpanded] = useState<string[]>([]);

  const router = useRouter();

  const { pathname } = router;

  const storeColors = useSelector(
    (state: RootState) => state.colorsReducer.colors
  );

  const toggleExpanded =
    (filterKey: string) => (_event: any, isExpanded: boolean) => {
      if (isExpanded) {
        setExpanded([filterKey]);
      } else {
        setExpanded([]);
      }
    };

  let list;

  list = getFilterAccordions(
    filters,
    slug as string[],
    queryParameters,
    storeColors,
    t,
    expanded,
    toggleExpanded,
    pathname
  );

  return (
    <>
      <RemoveLinkWrapper>
        <LinkHandler
          href={{
            pathname,
            query: {
              slug,
              ...(queryParameters['sort_by']
                ? { sort_by: queryParameters['sort_by'] }
                : {}),
            },
          }}
          shallow={true}>
          <RemoveFiltersTypography datatype="caption_1">
            {t('catalog:cta.remove-filters')}
          </RemoveFiltersTypography>
        </LinkHandler>
      </RemoveLinkWrapper>
      <Content>{list}</Content>
    </>
  );
};

export default FiltersList;

const priceCustomRangeHandler = (priceValue: string, router: any): void => {
  const { slug, ...queryParams } = router.query;
  const { newQueryParameters: newQueryParams } = makeCrossFilterHref(
    'price',
    {
      slug: priceValue,
      tag: '',
      label: '',
    },
    queryParams
  );

  const newUrl = {
    pathname: router.pathname,
    query: {
      slug: slug as string[],
      ...queryParams,
      ...newQueryParams,
    },
  };

  router.push(newUrl, undefined, { shallow: true });
};

const getFilterAccordions = (
  filters: ICmsTagsGroup | null,
  slug: string[],
  validQueryParams: { [key: string]: string | string[] | undefined },
  storeColors: {
    [k: string]: IColorFields;
  },
  t: any,
  expanded: string[] | null,
  toggleExpanded: (
    filterKey: string
  ) => (_event: any, isExpanded: boolean) => void,
  pathname: string
): JSX.Element[] => {
  const isB2b = process.env.NEXT_PUBLIC_B2B === '1';

  let filterByList = [];

  let filterLinks: JSX.Element[] = [];
  let isPriceLinkActive = false;
  for (const filterKey in filters) {
    if (filterKey.toLowerCase() === 'gender') {
      //  il filtro 'gender' non lo mostro in FilterBy
      continue;
    }
    if (filters[filterKey]) {
      filterLinks = filters[filterKey].map((filter, i) => {
        let { active, newQueryParameters } = makeCrossFilterHref(
          filterKey,
          filter,
          validQueryParams
        );

        let bgColor,
          bgImageUrl = null;
        if (storeColors && filterKey === 'color') {
          if (filter.label) {
            const colorKey = (filter.label || filter.slug).toLowerCase();
            bgColor = storeColors[colorKey]
              ? storeColors[colorKey].backgroundColor
              : filter.label;
            bgImageUrl =
              storeColors[colorKey] && storeColors[colorKey].backgroundImage
                ? storeColors[colorKey].backgroundImage?.fields?.file?.url
                : null;
          }
        }

        if (filterKey === 'price' && active) {
          isPriceLinkActive = true;
        }

        return (
          <FilterLinkWrapper key={`wrapper${filter.slug}-${i}`}>
            <FilterLink
              key={`${filter.slug}-${i}`}
              filterInfo={{
                filterKey,
                filter,
                active,
                bgColor,
                bgImageUrl,
              }}
              pathInfo={{
                basePath: pathname,
                slug: slug as string[],
                queryParams: newQueryParameters,
                shallow: true,
              }}
              noFollow={true}
              bulletLink={true}></FilterLink>
          </FilterLinkWrapper>
        );
      });
    }

    if (filterLinks.length > 0) {
      const accordionHeading =
        filterKey.toLowerCase() === 'artisan' && isB2b
          ? t('artisanB2b')
          : t(`catalog:${`filters.${filterKey}`}`);

      filterByList.push(
        <div style={{ marginBottom: '4px' }} key={`wrapperfilter-${filterKey}`}>
          <AccordionFilter
            key={`filter-${filterKey}`}
            heading={capitalize(accordionHeading) || ''}
            template="collectionFilters"
            onChange={toggleExpanded(filterKey)}
            expanded={expanded && expanded.includes(filterKey) ? true : false}
            indent={false}
            active={Object.keys(validQueryParams).includes(filterKey)}>
            <AccordionContentWrapper
              className={'customWrapper'}
              noMaxHeight={UNLIMITED_DRAWER.includes(filterKey)}>
              {filterKey === 'color' ? (
                <ColorsContainer>{filterLinks}</ColorsContainer>
              ) : (
                <>
                  {filterLinks}
                  {filterKey === 'price' && (
                    <RangeSelectorWrapper>
                      <RangeSelector
                        active={
                          'price' in validQueryParams && !isPriceLinkActive
                        }
                        applyHandler={priceCustomRangeHandler}
                      />
                    </RangeSelectorWrapper>
                  )}
                </>
              )}
            </AccordionContentWrapper>
          </AccordionFilter>
        </div>
      );
    }
  }
  return filterByList;
};
